import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createContactApi } from "../contacts/contacts-api";

// Minimal Create Contact popup — shared across Account view, Lead view, and
// anywhere else that needs to quick-create a contact linked to a record.
// `linkTo` tells the popup (and the "More details" full-page handoff) which
// record to attach the new contact to: { type: "Account" | "Lead", id, label }.
const CreateContactModal = ({ isOpen, onClose, onCreated, linkTo, notify }) => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [designation, setDesignation] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setDesignation("");
    setMobile("");
    setEmail("");
    setErrors({});
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const validate = () => {
    const nextErrors = {};
    if (!firstName.trim()) nextErrors.first_name = "First name is required.";
    if (!lastName.trim()) nextErrors.last_name = "Last name is required.";
    if (!designation.trim()) nextErrors.designation = "Designation is required.";
    if (!mobile.trim() && !email.trim()) {
      nextErrors.contact_info = "Enter at least a mobile number or email.";
    }
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        nextErrors.email = "Please enter a valid email address.";
      }
    }
    if (mobile.trim()) {
      const cleanNum = mobile.trim().replace(/[^0-9]/g, "");
      if (cleanNum.length < 7 || cleanNum.length > 18) {
        nextErrors.mobile = "Mobile number must be between 7 and 18 digits.";
      }
    }
    return nextErrors;
  };

  const handleSubmit = async () => {
    const nextErrors = validate();
    if (!linkTo?.id) {
      notify?.(`Unable to link this contact to a ${linkTo?.type?.toLowerCase() || "record"}. Please reopen this page and try again.`, false);
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        designation: designation.trim(),
        contact_type: linkTo.type || "",
        account_id: linkTo.type === "Account" ? linkTo.id : null,
        lead_id: linkTo.type === "Lead" ? linkTo.id : null,
        account_ids: linkTo.type === "Account" ? [linkTo.id] : [],
        lead_ids: linkTo.type === "Lead" ? [linkTo.id] : [],
        mobiles: mobile.trim() ? [{ mobile_number: mobile.trim(), type: "Primary" }] : [],
        emails: email.trim() ? [{ email: email.trim(), type: "Primary" }] : [],
      };
      const response = await createContactApi(payload);
      if (response?.data?.success) {
        notify?.(response.data.message || "Contact created successfully", true);
        onClose();
        resetForm();
        onCreated?.(response);
      } else {
        const errorMsg = response?.data?.error
          ? typeof response.data.error === "object"
            ? Object.values(response.data.error).flat().join(", ")
            : response.data.error
          : response?.data?.message || "Failed to create contact";
        notify?.(errorMsg, false);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.error
        ? typeof error.response.data.error === "object"
          ? Object.values(error.response.data.error).flat().join(", ")
          : error.response.data.error
        : error?.response?.data?.message || "Failed to create contact";
      notify?.(errorMsg, false);
    } finally {
      setSaving(false);
    }
  };

  const handleMoreDetails = () => {
    onClose();
    navigate("/contacts/create", {
      state: {
        contactFrom: linkTo?.type,
        moduleDetail: { value: linkTo?.id, label: linkTo?.label },
        prefill: {
          first_name: firstName,
          last_name: lastName,
          designation,
          mobile,
          email,
        },
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          width: "480px",
          maxWidth: "92vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderBottom: "1px solid #ebebeb",
          }}
        >
          <div className="fs-16 fw-600 text-dkblue">Create Contact</div>
          <span
            style={{ cursor: "pointer", fontSize: "18px", color: "#8790A1" }}
            onClick={handleClose}
          >
            ✕
          </span>
        </div>

        <div style={{ padding: "24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
                First Name<span style={{ color: "#ff4444" }}>*</span>
              </div>
              <input
                type="text"
                className="form-control"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name..."
              />
              {errors.first_name && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.first_name}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
                Last Name<span style={{ color: "#ff4444" }}>*</span>
              </div>
              <input
                type="text"
                className="form-control"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name..."
              />
              {errors.last_name && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.last_name}</div>}
            </div>
          </div>

          <div>
            <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
              Designation<span style={{ color: "#ff4444" }}>*</span>
            </div>
            <input
              type="text"
              className="form-control"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              placeholder="Enter designation..."
            />
            {errors.designation && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.designation}</div>}
          </div>

          <div>
            <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
              Mobile Number
            </div>
            <input
              type="text"
              className="form-control"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter mobile number..."
            />
            {errors.mobile && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.mobile}</div>}
          </div>

          <div>
            <div className="fs-13 fw-500" style={{ marginBottom: "6px", color: "#21272c" }}>
              Email
            </div>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email..."
            />
            {errors.email && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.email}</div>}
            {errors.contact_info && <div className="fs-12" style={{ color: "#ff4444", marginTop: "4px" }}>{errors.contact_info}</div>}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 24px", borderTop: "1px solid #ebebeb" }}>
          <span
            style={{ color: "#3D4AF5", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
            onClick={handleMoreDetails}
          >
            More details →
          </span>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              style={{ backgroundColor: "#fff", color: "#21272c", border: "1px solid #d4dde7", borderRadius: "100px", padding: "8px 24px", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              style={{ backgroundColor: "#6B46FE", color: "#fff", border: "none", borderRadius: "100px", padding: "8px 24px", fontWeight: 600, fontSize: "14px", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateContactModal;
